import { ThemeProvider } from '@front10/base-elements';
import * as React from 'react';
import { getAirlines } from '../../../_integrations/everymundo/airlines';
import { getBlackoutDates } from '../../../_integrations/everymundo/blackoutDates';
import { getAirports } from '../../../_integrations/everymundo/airports';
import {
  useTrackingChange,
  VerticalBookingActions
} from '../../../_integrations/everymundo/tracking/booking';
import withCommonEMContainer from '../_commons/CommonEMContainer';
import { EmotionCache } from '../_commons/emotionCache';
import { AirportMarketFilter } from './airports';
import { BookingMaskContainer as BookingMask } from './BookingMask';
import { DatePickerLabelsProvider } from './DatePickerLabelsProvider';
import { prepareAirportForMarket } from './utils';
import { OriginDestinationLogic } from '../_commons/filters';
import { deepmerge } from '../../../em-ecosystem';

function Container(props) {
  const track = useTrackingChange();

  const [activePanel, setActivePanel] = React.useState(0);
  const panels = props.componentsConfiguration?.panels ?? [];

  let prePopulation = deepmerge(props.prePopulation, panels[activePanel].prePopulation ?? {}, {
    arrayMerge: (_, source) => source
  });

  const businessRules = deepmerge(props.businessRules, panels[activePanel].businessRules ?? {}, {
    arrayMerge: (_, source) => source
  });

  const fetchAirlines = React.useCallback(
    ({ selectedOrigin, selectedDestination }) =>
      getAirlines(
        prePopulation.airlines,
        selectedOrigin,
        selectedDestination,
        props.integrationSettings,
        props.meta,
        props.pageContext
      ),
    [prePopulation, props.integrationSettings, props.meta, props.pageContext]
  );

  const fetchBlackoutDates = React.useCallback(
    ({ selectedOrigin, selectedDestination, isRoundtrip }) => {
      return getBlackoutDates(
        selectedOrigin,
        selectedDestination,
        isRoundtrip,
        props.integrationSettings,
        props.meta,
        props.pageContext
      );
    },
    [props.integrationSettings, props.meta, props.pageContext]
  );

  const fetchAirports = React.useCallback(
    ({
      airportType,
      selectedOrigin,
      selectedDestination,
      filterFields = AirportMarketFilter.NONE
    }) => {
      const airport = prepareAirportForMarket(prePopulation.airport, airportType, filterFields);
      const { originDestinationLogic = 'same' } = props;

      const avoidCall = originDestinationLogic === OriginDestinationLogic.SAME;
      if (avoidCall && airportType === 'DESTINATION' && !selectedOrigin) {
        return Promise.resolve([]);
      }

      const marketValue =
        prePopulation.autoSettings?.market ??
        (Array.isArray(prePopulation.markets) ? prePopulation.markets[0] : prePopulation.markets);

      return getAirports(
        {
          market: marketValue,
          ...airport,
          airlineIataCode: prePopulation.airlineIataCode,
          language: prePopulation.languageCode,
          routeOption: {
            airportType,
            selectedOrigin,
            selectedDestination
          }
        },
        props.integrationSettings,
        props.meta,
        props.pageContext
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      prePopulation,
      props.integrationSettings,
      props.meta,
      props.pageContext,
      props.originDestinationLogic
    ]
  );
  const bookingMaskProps = {
    ...props,
    prePopulation,
    businessRules,
    passengersRules: panels[activePanel]?.passengersRules ?? props.passengersRules
  };
  const {
    valueMap = {
      travelClasses: {
        BUSINESS: 'b',
        ECONOMY: 'e',
        FIRST_CLASS: 'fc'
      },
      journeyTypes: {
        ROUND_TRIP: 'rt',
        ONE_WAY: 'ow',
        MULTI_CITY: 'mc'
      }
    }
  } = props;

  const travelClass = prePopulation.travelClass;
  const journeyType = prePopulation.flightType;

  if (valueMap.travelClasses[travelClass]) {
    bookingMaskProps.businessRules.travelClasses.defaultValue = valueMap.travelClasses[travelClass];
  }

  if (valueMap.journeyTypes[journeyType]) {
    bookingMaskProps.businessRules.journeyTypes.defaultValue = valueMap.journeyTypes[journeyType];
  }

  if (Object.keys(prePopulation).includes('discountCode')) {
    bookingMaskProps.businessRules.promoCode = prePopulation.discountCode;
  }

  if (Object.keys(prePopulation).includes('passengerCount')) {
    bookingMaskProps.businessRules.travelers.defaultSelection.age1 = prePopulation.passengerCount;
  }

  if (Object.keys(prePopulation).includes('flexibleDates')) {
    bookingMaskProps.componentsConfiguration.flexibleDatesSelector.enabled =
      prePopulation.flexibleDates;
  }

  const container = (
    <ThemeProvider theme={props.theme.designTokens || props.theme}>
      <DatePickerLabelsProvider
        localizationData={props.localizationData}
        translate={props.translate}
        weekdayFormat={
          bookingMaskProps?.componentsConfiguration?.dateSelector?.weekdayFormat
            ? bookingMaskProps?.componentsConfiguration?.dateSelector?.weekdayFormat
            : 'min'
        }
        monthFormatForElements={
          prePopulation.monthFormatForElements ?? ['zh', 'zh-cn', 'zh-hk', 'zh-tw']
        }
      >
        <BookingMask
          onSubmit={(data) => {
            track(VerticalBookingActions.SEARCH_FLIGHTS, {
              ...data,
              businessRules: businessRules
            });
          }}
          onOriginChange={(data) => track(VerticalBookingActions.CHANGE_ORIGIN, data)}
          onAirlineChange={(data) => track(VerticalBookingActions.CHANGE_AIRLINE, data)}
          onDestinationChange={(data) => track(VerticalBookingActions.CHANGE_DESTINATION, data)}
          onJourneyTypeChange={(data) => track(VerticalBookingActions.CHANGE_JOURNEY_TYPE, data)}
          onDepartureDateChange={(data) =>
            track(VerticalBookingActions.CHANGE_DEPARTURE_DATE, data)
          }
          onReturnDateChange={(data) => track(VerticalBookingActions.CHANGE_RETURN_DATE, data)}
          fetchAirports={fetchAirports}
          fetchAirlines={fetchAirlines}
          fetchBlackoutDates={fetchBlackoutDates}
          filterFields={prePopulation.filterFields}
          setActivePanel={setActivePanel}
          activePanel={activePanel}
          {...bookingMaskProps}
        />
      </DatePickerLabelsProvider>
    </ThemeProvider>
  );

  return props?.disabledCache ? (
    container
  ) : (
    <EmotionCache
      direction={props.direction}
      componentName={Container.displayName}
      tenant={props?.pageContext?.provider}
    >
      {container}
    </EmotionCache>
  );
}

Container.displayName = 'BookingMaskContainer';

export const BookingMaskContainer = withCommonEMContainer(Container);
